import { behaviourTone, SEVERITY_COLOR } from "../../lib/traceReview.js";

// The triage listing's own marks: its chips, the disclosure every row opens
// by, a severity as a chip or a word, a count, the inset an opened case keeps
// and the turn axis its timelines draw on. The Triage tab defines them; the
// Findings, Conduct and Triage-lite tabs borrow them, so the lens reads as one
// list whichever tab is open.
export const CHIP = "inline-flex shrink-0 items-center justify-center whitespace-nowrap rounded-[5px] border px-[5px] leading-[15px]";
export const CAPS_CHIP = `${CHIP} text-[9px] tracking-[0.08em] uppercase`;
export const FILE_LABEL = "text-[12px] leading-[1.4] text-[color:var(--muted)]";
export const ACTION_CHIP = "shrink-0 cursor-pointer rounded-[5px] border border-[color:var(--line)] bg-transparent px-[6px] text-[11px] leading-[15px] text-[color:var(--muted)] transition-colors hover:border-[color:var(--line-strong)] hover:text-[color:var(--ink)]";
// A card laid over the list and hung from what opened it: a picked bar's
// findings, a group's standards.
export const POPOVER_CARD = "rounded-[10px] border border-[color:var(--line-strong)] bg-[color:var(--panel)] shadow-[0_8px_24px_rgba(0,0,0,0.14)]";

// Filled where the pass asks for a look now, outlined below it, dashed where
// it has not reached.
export const RUNG_CHIP = {
  look_now: "border-[color:var(--ink)] bg-[color:var(--ink)] text-[color:var(--bg)]",
  worth_a_look: "border-[color:var(--ink)] text-[color:var(--ink)]",
  routine: "border-[color:var(--line-strong)] text-[color:var(--muted)]",
  noise: "border-[color:var(--line)] text-[color:var(--muted-soft)]",
  unplaced: "border-dashed border-[color:var(--line-strong)] text-[color:var(--muted)]",
};

export const CHECK_CHIP = {
  holds: ["True", "border-[color:var(--ink)] bg-[color:var(--ink)] text-[color:var(--bg)]"],
  overstated: ["Partly true", "border-[color:var(--line-strong)] text-[color:var(--ink)]"],
  misread: ["False", "border-[color:var(--line-strong)] text-[color:var(--muted)]"],
  unchecked: ["Not checked", "border-dashed border-[color:var(--line-strong)] text-[color:var(--muted)]"],
};

export const SEVERITY_SHORT = { critical: "Crit", high: "High", medium: "Med", low: "Low" };

export function Chevron({ open }) {
  return (
    <svg viewBox="0 0 10 10" className={`h-[10px] w-[10px] shrink-0 transition-transform duration-150 ${open ? "rotate-90" : ""}`} aria-hidden="true">
      <path d="M3.5 2L6.5 5L3.5 8" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// The one disclosure the listing uses, always at the row's left edge: a band,
// a case and a finding open the same way, so the indent alone says what holds
// what.
export function Disclosure({ open, label, onToggle, ...attrs }) {
  return (
    <button
      type="button"
      {...attrs}
      aria-expanded={open}
      aria-label={label}
      onClick={(e) => { e.stopPropagation(); onToggle(); }}
      className="grid h-[15px] w-[14px] shrink-0 cursor-pointer place-items-center border-0 bg-transparent p-0 text-[color:var(--muted)] hover:text-[color:var(--ink)]"
    >
      <Chevron open={open} />
    </button>
  );
}

export function SeverityChip({ severity, ...attrs }) {
  const label = SEVERITY_SHORT[severity];
  if (!label) return <span {...attrs} className="w-[38px] shrink-0 text-center text-[9px] leading-[15px] text-[color:var(--muted-soft)]" aria-hidden="true">{"\u2014"}</span>;
  const tone = behaviourTone(severity);
  return (
    <span {...attrs} data-triage-severity={severity} className={`${CAPS_CHIP} w-[38px] border-current`} style={{ color: tone === "calm" ? "var(--muted)" : SEVERITY_COLOR[tone] }}>
      {label}
    </span>
  );
}

export function CountChip({ count }) {
  return (
    <span className="flex min-w-[44px] shrink-0 justify-end">
      {count > 1 ? <span data-triage-count={count} className={`${CHIP} border-[color:var(--line-strong)] text-[9px] tabular-nums text-[color:var(--muted)]`}>{"\u00d7"}{count.toLocaleString()}</span> : null}
    </span>
  );
}

// Everything an opened case holds sits in one inner column, under the case's
// title: the case file, where the findings' evidence sits, and the numbered
// findings, whose own disclosure opens at that column's edge. The Findings tab
// hangs the same pieces under a session's summary, at its own inset.
export const CASE_INSET = 76;
export const turnSpanLabel = (span) => (!span ? null : span.from === span.to ? `T${span.from}` : `T${span.from}\u2013T${span.to}`);

// A finding's severity as a word in its tone: a list entry, lighter than the
// bordered chip that heads a case.
export function SeverityWord({ severity, quiet = false }) {
  const label = SEVERITY_SHORT[severity];
  const tone = behaviourTone(severity);
  return (
    <span
      data-triage-severity={severity || undefined}
      className="w-[34px] shrink-0 text-[9px] leading-[15px] tracking-[0.08em] uppercase"
      style={{ color: !label ? "var(--muted-soft)" : quiet || tone === "calm" ? "var(--muted)" : SEVERITY_COLOR[tone] }}
    >
      {label || "\u2014"}
    </span>
  );
}

// Five labels along a session's turns, numbered from zero as the Lanes axis
// prints them: each names the turn that starts where it stands, the last the
// session's last turn, the ends pinned to the edges.
export function TurnTicks({ total }) {
  const at = total >= 8 ? [0, 0.25, 0.5, 0.75, 1] : [0, 1];
  return (
    <div className="relative h-[12px] font-mono text-[9px] leading-[12px] tabular-nums text-[color:var(--muted-soft)]" aria-hidden="true">
      {at.map((p, i) => {
        const style = i === 0 ? { left: 0 } : i === at.length - 1 ? { right: 0 } : { left: `${p * 100}%`, transform: "translateX(-50%)" };
        return <span key={p} className="absolute top-0 whitespace-nowrap" style={style}>T{Math.min(Math.max(0, total - 1), Math.round(p * total))}</span>;
      })}
    </div>
  );
}

// A session's turns in a fixed number of stretches, so every session's bars
// are one width: a bar is as tall as the number of the case's findings citing
// inside its stretch, and the baseline marks every stretch, cited or not.
export const TIMELINE_BINS = 48;

export const BASELINE = `repeating-linear-gradient(to right, var(--line) 0, var(--line) calc(100% / ${TIMELINE_BINS} - 2px), transparent calc(100% / ${TIMELINE_BINS} - 2px), transparent calc(100% / ${TIMELINE_BINS}))`;
